import type { ComponentType } from 'react';

/**
 * Client-side module contract (ROADMAP §1.4).
 *
 * A module owns `apps/web/src/modules/<id>/` and exposes exactly three things to the
 * shell: a nav entry, routes, and the Yjs docs it needs subscribed. Nothing else in the
 * app imports module internals.
 */
export interface NavEntry {
  label: string;
  /** Path the nav item links to; must match one of the module's routes. */
  path: string;
  /** Emoji or icon name rendered in the nav. */
  icon?: string;
  /** Lower sorts first; defaults to 100. */
  order?: number;
  /** Opts an entry out of both the mobile bottom-tab bar and the desktop top nav
   * (`apps/web/src/chrome/`) — used by digest, which still needs the entry to exist (it's
   * a valid nav destination, just not a tab) because it's reachable from the wordmark and
   * a push deep-link (WS-14). A module that wants no nav presence at all (household, dev
   * — owner call 2026-07-31, docs/DECISIONS.md) just omits `nav` entirely instead. */
  hideInTabs?: boolean;
}

export interface ModuleRoute {
  /** Route pattern, e.g. `/lists` or `/lists/:listId`. */
  path: string;
  component: ComponentType;
}

export interface WebModule {
  id: string;
  nav?: NavEntry;
  routes?: readonly ModuleRoute[];
  /**
   * Yjs doc names the module needs open while it is mounted. WS-1 turns these into
   * providers; a trailing `:` means "docs opened on demand with this prefix".
   */
  docs?: readonly string[];
}

const modules = new Map<string, WebModule>();

export function registerModule(module: WebModule): void {
  if (modules.has(module.id)) {
    throw new Error(`Module "${module.id}" is already registered`);
  }
  modules.set(module.id, module);
}

export function getModules(): WebModule[] {
  return [...modules.values()];
}

/** Test helper — production code registers once at startup. */
export function clearModules(): void {
  modules.clear();
}

/** A nav entry plus the id of the module that registered it. The chrome needs the id to
 * resolve a drawn icon for the entry (`chrome/icons/NavIcon.tsx`, 2026-08-01) — the registry
 * contract itself is unchanged, modules still declare only a plain emoji in `nav.icon`. */
export interface ResolvedNavEntry extends NavEntry {
  moduleId: string;
}

/** Nav entries in display order. Modules without a nav entry are invisible in the chrome. */
export function getNavEntries(): ResolvedNavEntry[] {
  return getModules()
    .flatMap((module) => (module.nav ? [{ ...module.nav, moduleId: module.id }] : []))
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));
}

export function getRoutes(): ModuleRoute[] {
  return getModules().flatMap((module) => [...(module.routes ?? [])]);
}

/** Distinct doc names/prefixes across all modules — the sync layer's subscription set. */
export function getSubscribedDocs(): string[] {
  return [...new Set(getModules().flatMap((module) => [...(module.docs ?? [])]))];
}
